import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke } from '@saas-pages/shared/public/runtime'
import { localeCode, useLocale, useT } from '@/shared/i18n'
import { approvedProductionCalendars, productionCalendarYearRoute } from '@/shared/production-calendar'
import {
  MAX_WORKING_DAYS,
  calculateCalendarDate,
  calculateWorkingDate,
  type CalendarDateResult,
  type DateUnit,
  type Direction,
  type WorkingCalendarBasis,
  type WorkingDateCalculation,
  type WorkingDateResult,
} from '@/pages/add-subtract-date/model/addSubtractDate'
import { startWorkingDateOperation } from '@/pages/add-subtract-date/model/addSubtractDateAnalytics'

const UTILITY = 'add-subtract-date'

const WORKING_ANALYTICS_DELAY_MS = 800

type CalculationMode = 'calendar' | 'working'

interface QuickPreset {
  key: string
  direction: Direction
  amount: number
  unit: DateUnit
}

function todayLocalDate(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function toLocalDate(value: string): Date | null {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? null : date
}

function toInputDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseAmount(value: string): number | null {
  if (!value.trim()) return null
  const amount = Number(value)
  return Number.isInteger(amount) && amount >= 0 ? amount : null
}

function formatDate(value: string, locale: string): string {
  const date = toLocalDate(value)
  if (!date) return value
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function formatWeekday(value: string, locale: string): string {
  const date = toLocalDate(value)
  if (!date) return value
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date)
}

function formatCalendarMonth(date: Date | null, locale: string): string {
  if (!date) return '---'
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date)
}

function formatCalendarDay(date: Date | null): string {
  return date ? String(date.getDate()).padStart(2, '0') : '--'
}

export function AddSubtractDatePage() {
  const t = useT()
  const { locale } = useLocale()
  const localeName = localeCode(locale)
  const [calculationMode, setCalculationMode] = useState<CalculationMode>('calendar')
  const [baseDate, setBaseDate] = useState(() => toInputDate(todayLocalDate()))
  const [direction, setDirection] = useState<Direction>('add')
  const [amount, setAmount] = useState('7')
  const [unit, setUnit] = useState<DateUnit>('days')
  const [workingBasis, setWorkingBasis] = useState<WorkingCalendarBasis>('weekends_only')

  const parsedBaseDate = useMemo(() => toLocalDate(baseDate), [baseDate])
  const parsedAmount = useMemo(() => parseAmount(amount), [amount])
  const amountInvalid = amount.trim() !== '' && (
    parsedAmount === null || (calculationMode === 'working' && parsedAmount > MAX_WORKING_DAYS)
  )
  const calendarResult = useMemo(() => {
    if (calculationMode !== 'calendar' || parsedAmount === null) return null
    return calculateCalendarDate(baseDate, direction, parsedAmount, unit)
  }, [baseDate, calculationMode, direction, parsedAmount, unit])

  const workingResult = useMemo<WorkingDateCalculation | null>(() => {
    if (calculationMode !== 'working' || !baseDate || amount.trim() === '') return null
    return calculateWorkingDate(baseDate, direction, Number(amount), workingBasis, approvedProductionCalendars)
  }, [amount, baseDate, calculationMode, direction, workingBasis])

  // The result updates on every keystroke; report only the value that settled.
  useEffect(() => {
    if (!workingResult) return
    const timeoutId = window.setTimeout(() => {
      const operation = startWorkingDateOperation()
      if (workingResult.kind === 'success') {
        operation.success(0)
      } else if (workingResult.kind === 'invalid-input') {
        operation.error('invalid_input')
      } else if (workingResult.kind === 'unsupported-input') {
        operation.error('unsupported_input')
      } else {
        operation.error('processing_failed')
      }
    }, WORKING_ANALYTICS_DELAY_MS)
    return () => window.clearTimeout(timeoutId)
  }, [workingResult])

  const quickPresets = useMemo<QuickPreset[]>(() => [
    { key: 'week', direction: 'add', amount: 7, unit: 'days' },
    { key: 'two-weeks-back', direction: 'subtract', amount: 14, unit: 'days' },
    { key: 'month', direction: 'add', amount: 1, unit: 'months' },
  ], [])

  const handleBaseDateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setBaseDate(event.target.value)
  }, [])

  const handleAmountChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setAmount(event.target.value)
  }, [])

  const handlePreset = useCallback((preset: QuickPreset) => {
    setDirection(preset.direction)
    setAmount(String(preset.amount))
    setUnit(preset.unit)
    toolInvoke(UTILITY, { action: 'preset', preset: preset.key })
  }, [])

  const handleClear = useCallback(() => {
    setCalculationMode('calendar')
    setBaseDate('')
    setDirection('add')
    setAmount('')
    setUnit('days')
    setWorkingBasis('weekends_only')
    toolInvoke(UTILITY, { action: 'reset' })
  }, [])

  return (
    <div className="mx-auto max-w-5xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
          {t.addSubtractDate.title}
        </h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.addSubtractDate.description}
        </p>
      </section>

      <Card className="ym-hide-content ym-disable-keys overflow-hidden">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-emerald-400" />
        <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.95fr)] lg:grid-rows-[auto_1fr]">
          <div className="space-y-3 p-4 sm:space-y-4 sm:p-6 lg:col-start-1 lg:row-start-1 lg:border-r lg:pb-0">
            <fieldset>
              <legend className="sr-only">
                {t.addSubtractDate.calculationMode}
              </legend>
              <div className="grid grid-cols-2 gap-1 rounded-lg border bg-background/70 p-1">
                {(['calendar', 'working'] as CalculationMode[]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setCalculationMode(mode)}
                    className={cn(
                      'min-h-10 rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      calculationMode === mode
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    {mode === 'calendar'
                      ? t.addSubtractDate.calendarMode
                      : t.addSubtractDate.workingMode}
                  </button>
                ))}
              </div>
            </fieldset>

            <DateField
              id="add-subtract-base-date"
              label={t.addSubtractDate.baseDate}
              value={baseDate}
              date={parsedBaseDate}
              locale={localeName}
              onChange={handleBaseDateChange}
            />

            <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] items-end gap-3">
              <fieldset>
                <legend className="mb-1 text-sm font-medium">{t.addSubtractDate.direction}</legend>
                <div className="grid grid-cols-2 gap-1 rounded-lg border bg-background/70 p-0.5">
                {(['add', 'subtract'] as Direction[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setDirection(item)}
                    className={cn(
                      'h-9 rounded-md px-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      direction === item
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    {item === 'add' ? t.addSubtractDate.add : t.addSubtractDate.subtract}
                  </button>
                ))}
                </div>
              </fieldset>

              <label htmlFor="add-subtract-amount" className="block">
              <span className="mb-1 block text-sm font-medium">{t.addSubtractDate.amount}</span>
              <input
                id="add-subtract-amount"
                type="number"
                inputMode="numeric"
                min="0"
                max={calculationMode === 'working' ? MAX_WORKING_DAYS : undefined}
                step="1"
                value={amount}
                onChange={handleAmountChange}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-base shadow-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring sm:text-sm"
              />
              </label>
            </div>

            {amountInvalid && (
              <p className="text-sm text-destructive" role="alert">
                {calculationMode === 'working'
                  ? t.addSubtractDate.errors.invalidWorkingAmount
                  : t.addSubtractDate.errors.invalidAmount}
              </p>
            )}

            {calculationMode === 'calendar' && <UnitControls unit={unit} onUnitChange={setUnit} />}

            {calculationMode === 'calendar' && parsedBaseDate && parsedAmount !== null && !calendarResult && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
                {t.addSubtractDate.errors.invalidResult}
              </div>
            )}
          </div>

          {calculationMode === 'calendar' ? (
            <CalendarResultPanel result={calendarResult} locale={localeName} />
          ) : (
            <WorkingResultPanel
              result={workingResult}
              locale={localeName}
              onUseWeekendsOnly={() => setWorkingBasis('weekends_only')}
            />
          )}

          <div className="space-y-4 border-t p-4 sm:p-6 lg:col-start-1 lg:row-start-2 lg:border-r lg:border-t-0">
            {calculationMode === 'calendar' ? (
              <QuickPresets quickPresets={quickPresets} onPreset={handlePreset} />
            ) : (
              <WorkingControls basis={workingBasis} onBasisChange={setWorkingBasis} />
            )}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="ghost" onClick={handleClear}>{t.common.clear}</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-xl font-semibold tracking-normal">{t.addSubtractDate.howToTitle}</h2>
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-6 text-muted-foreground">
          {t.addSubtractDate.howTo.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>

      <section className="mt-10 grid gap-8 border-t pt-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.7fr)]">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.addSubtractDate.faqTitle}</h2>
          <dl className="mt-4 space-y-4">
            {t.addSubtractDate.faq.map((item) => (
              <div key={item.question}>
                <dt className="font-medium">{item.question}</dt>
                <dd className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
        <aside>
          <h2 className="text-xl font-semibold tracking-normal">{t.addSubtractDate.relatedTitle}</h2>
          <div className="mt-4 space-y-2">
            {t.addSubtractDate.related.map((item) => (
              <a key={item.href} href={item.href} className="block rounded-md border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent">
                <span className="text-sm font-medium">{item.title}</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">{item.description}</span>
              </a>
            ))}
          </div>
        </aside>
      </section>
    </div>
  )
}

function UnitControls({
  unit,
  onUnitChange,
}: {
  unit: DateUnit
  onUnitChange: (unit: DateUnit) => void
}) {
  const t = useT()
  return (
    <fieldset>
      <legend className="mb-1 text-sm font-medium">{t.addSubtractDate.unit}</legend>
      <div className="grid grid-cols-4 gap-1 sm:gap-2">
          {(['days', 'weeks', 'months', 'years'] as DateUnit[]).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onUnitChange(item)}
              className={cn(
                'h-10 rounded-md border px-0.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-3 sm:text-sm',
                unit === item
                  ? 'border-primary/60 bg-primary/10 text-foreground'
                  : 'bg-background/70 text-muted-foreground hover:border-primary/40 hover:bg-accent hover:text-foreground',
              )}
            >
              {t.addSubtractDate.units[item]}
            </button>
          ))}
      </div>
    </fieldset>
  )
}

function QuickPresets({
  quickPresets,
  onPreset,
}: {
  quickPresets: readonly QuickPreset[]
  onPreset: (preset: QuickPreset) => void
}) {
  const t = useT()
  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold tracking-normal">{t.addSubtractDate.quickActions}</h2>
      <div className="grid gap-2 sm:grid-cols-3">
        {quickPresets.map((preset) => (
          <button key={preset.key} type="button" onClick={() => onPreset(preset)} className="rounded-md border bg-background/70 px-3 py-2.5 text-left text-sm font-medium transition-colors hover:border-primary/40 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {t.addSubtractDate.presets[preset.key as keyof typeof t.addSubtractDate.presets]}
          </button>
        ))}
      </div>
    </div>
  )
}

function WorkingControls({
  basis,
  onBasisChange,
}: {
  basis: WorkingCalendarBasis
  onBasisChange: (basis: WorkingCalendarBasis) => void
}) {
  const t = useT()
  return (
    <>
      <fieldset>
        <legend className="mb-2 text-sm font-semibold tracking-normal">{t.addSubtractDate.calendarBasis}</legend>
        <div className="grid gap-2">
          {([
            ['weekends_only', t.addSubtractDate.weekendsOnlyBasis],
            ['ru_federal_five_day', t.addSubtractDate.federalBasis],
          ] as const).map(([value, label]) => (
            <label key={value} className={cn('flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors', basis === value ? 'border-primary/50 bg-primary/10' : 'bg-background/70 hover:border-primary/40')}>
              <input type="radio" name="add-subtract-working-basis" value={value} checked={basis === value} onChange={() => onBasisChange(value)} className="mt-0.5 h-4 w-4 border-gray-300 text-primary focus:ring-primary" />
              <span className="font-medium">{label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <p className="rounded-md border bg-background/70 px-3 py-2 text-xs leading-5 text-muted-foreground">
        {basis === 'weekends_only'
          ? t.addSubtractDate.weekendsOnlyNote
          : t.addSubtractDate.federalBasisNote}
      </p>
    </>
  )
}

function DateField({ id, label, value, date, locale, onChange }: {
  id: string
  label: string
  value: string
  date: Date | null
  locale: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}) {
  return (
    <label htmlFor={id} className="block transition-colors focus-within:border-primary/50 sm:rounded-lg sm:border sm:bg-background/70 sm:p-3 sm:shadow-inner">
      <span className="mb-1 block text-sm font-medium sm:mb-3">{label}</span>
      <div className="grid gap-3 sm:grid-cols-[76px_minmax(10rem,1fr)] sm:items-center">
        <CalendarTile date={date} locale={locale} />
        <input id={id} type="date" min="0001-01-01" max="9999-12-31" value={value} onChange={onChange} className="h-11 w-full min-w-0 rounded-md border border-input bg-card px-3 pr-10 text-base shadow-sm sm:h-10 sm:text-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring" />
      </div>
    </label>
  )
}

function CalendarTile({ date, locale }: { date: Date | null; locale: string }) {
  return (
    <span className="hidden overflow-hidden rounded-md border bg-card text-center shadow-sm sm:block">
      <span className="block bg-primary px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-normal text-primary-foreground">{formatCalendarMonth(date, locale)}</span>
      <span className="block px-3 py-2 text-2xl font-bold leading-none tabular-nums">{formatCalendarDay(date)}</span>
    </span>
  )
}

function CalendarResultPanel({ result, locale }: { result: CalendarDateResult | null; locale: string }) {
  const t = useT()
  if (!result) return <EmptyResultPanel title={t.addSubtractDate.emptyResultTitle} description={t.addSubtractDate.emptyResultDescription} />

  const directionLabel = result.direction === 'add' ? t.addSubtractDate.added : t.addSubtractDate.subtracted
  const weekday = formatWeekday(result.resultDate, locale)
  return (
    <div className="space-y-4 bg-secondary/30 p-4 sm:space-y-5 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1">
      <ResultHeader date={result.resultDate} locale={locale} />
      <div className="hidden rounded-lg border bg-card/80 p-4 shadow-sm sm:block">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold tracking-normal">{t.addSubtractDate.operation}</h3>
          <span className="text-xs text-muted-foreground">{formatDate(result.baseDate, locale)} -&gt; {formatDate(result.resultDate, locale)}</span>
        </div>
        <DateShiftRail />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Metric label={t.addSubtractDate.finalDate} value={formatDate(result.resultDate, locale)} accent />
        <Metric label={t.addSubtractDate.weekday} value={weekday} />
        <Metric label={t.addSubtractDate.operation} value={`${directionLabel} ${result.amount} ${t.addSubtractDate.units[result.unit]}`} />
        <Metric label={t.addSubtractDate.baseDate} value={formatDate(result.baseDate, locale)} />
      </div>
      {result.clampedMonthEnd && <p className="rounded-md border border-amber-300 bg-amber-100 px-3 py-2 text-xs leading-5 text-amber-950 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">{t.addSubtractDate.monthEndNotice}</p>}
      <p className="text-xs leading-5 text-muted-foreground">{t.addSubtractDate.localNote}</p>
    </div>
  )
}

function WorkingResultPanel({ result, locale, onUseWeekendsOnly }: {
  result: WorkingDateCalculation | null
  locale: string
  onUseWeekendsOnly: () => void
}) {
  const t = useT()
  if (!result) return <EmptyResultPanel title={t.addSubtractDate.workingEmptyTitle} description={t.addSubtractDate.workingEmptyDescription} />
  if (result.kind !== 'success') {
    const coverageGap = result.kind === 'unsupported-input' && result.reason === 'coverage_gap'
    const description = coverageGap
      ? t.addSubtractDate.errors.coverageGap.replace('{years}', result.missingYears.join(', '))
      : result.kind === 'invalid-input'
        ? t.addSubtractDate.errors.invalidWorkingInput
        : result.kind === 'unsupported-input'
          ? t.addSubtractDate.errors.unsupportedDateRange
          : t.addSubtractDate.errors.processingFailure
    return (
      <div className="flex flex-col justify-between gap-6 bg-secondary/30 p-4 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:min-h-[360px]">
        <div>
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">{t.addSubtractDate.workingErrorEyebrow}</p>
          <h2 className="mt-3 text-2xl font-bold tracking-normal">{t.addSubtractDate.workingErrorTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
          {coverageGap && <p className="mt-3 rounded-md border border-amber-300/70 bg-amber-100/70 px-3 py-2 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">{t.addSubtractDate.noFederalFallback}</p>}
        </div>
        {coverageGap && <Button type="button" onClick={onUseWeekendsOnly}>{t.addSubtractDate.useWeekendsOnly}</Button>}
      </div>
    )
  }
  return <WorkingSuccess result={result} locale={locale} />
}

function WorkingSuccess({ result, locale }: { result: WorkingDateResult; locale: string }) {
  const t = useT()
  const skippedTotal = result.skippedOrdinaryWeekends + result.skippedFederalHolidaysOrTransfers
  return (
    <div className="space-y-4 bg-secondary/30 p-4 sm:space-y-5 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1">
      <ResultHeader date={result.resultDate} locale={locale} />
      <p className="rounded-md border bg-card/80 px-3 py-2 text-sm leading-6 text-muted-foreground">
        {result.amount === 0
          ? t.addSubtractDate.zeroWorkingExplanation
          : t.addSubtractDate.workingExplanation
            .replace('{amount}', String(result.amount))
            .replace('{skipped}', String(skippedTotal))}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Metric label={t.addSubtractDate.finalDate} value={formatDate(result.resultDate, locale)} accent />
        <Metric label={t.addSubtractDate.weekday} value={formatWeekday(result.resultDate, locale)} />
        <Metric label={t.addSubtractDate.skippedWeekends} value={result.skippedOrdinaryWeekends} />
        <Metric label={t.addSubtractDate.skippedFederal} value={result.skippedFederalHolidaysOrTransfers} />
      </div>
      <p className="text-xs leading-5 text-muted-foreground">
        {result.basis === 'weekends_only'
          ? t.addSubtractDate.weekendsOnlyGuardrail
          : t.addSubtractDate.federalGuardrail}
      </p>
      {result.coveredCalendars.map((calendar) => (
        <article key={calendar.year} className="rounded-lg border bg-card/80 p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold">{t.addSubtractDate.coverageYear.replace('{year}', String(calendar.year))}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{t.addSubtractDate.coverageMetadata.replace('{status}', calendar.status).replace('{date}', calendar.lastVerifiedAt)}</p>
            </div>
            <a href={productionCalendarYearRoute(calendar.year)} className="text-sm font-medium text-primary hover:underline">{t.addSubtractDate.openCalendarYear}</a>
          </div>
          <ul className="mt-3 space-y-1 text-xs leading-5 text-muted-foreground">
            {calendar.sources.filter((source) => source.kind === 'resolution').map((source) => (
              <li key={source.id}><a href={source.url} rel="noreferrer" target="_blank" className="underline decoration-border underline-offset-2 hover:text-foreground">{source.title}</a></li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  )
}

function ResultHeader({ date, locale }: { date: string; locale: string }) {
  const t = useT()
  return (
    <div>
      <p className="text-sm font-semibold text-primary">{t.addSubtractDate.resultReady}</p>
      <h2 className="mt-2 text-3xl font-bold tracking-normal sm:mt-3 sm:text-4xl">{formatDate(date, locale)}</h2>
      <p className="mt-2 text-base leading-7 text-muted-foreground">{formatWeekday(date, locale)}</p>
    </div>
  )
}

function EmptyResultPanel({ title, description }: { title: string; description: string }) {
  const t = useT()
  return (
    <div className="flex flex-col justify-between gap-6 bg-secondary/30 p-4 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:min-h-[360px]">
      <div>
        <p className="text-sm font-semibold text-primary">{t.addSubtractDate.resultReady}</p>
        <h2 className="mt-3 text-2xl font-bold tracking-normal">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}

function DateShiftRail() {
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3" aria-hidden="true">
      <span className="h-3 rounded-full bg-cyan-400/60 dark:bg-cyan-300/50" />
      <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">+ / -</span>
      <span className="h-3 rounded-full bg-emerald-400/60 dark:bg-emerald-300/50" />
    </div>
  )
}

function Metric({ label, value, accent = false }: { label: string; value: string | number; accent?: boolean }) {
  return (
    <div className={cn('rounded-md border px-3 py-2.5', accent ? 'bg-accent text-accent-foreground' : 'bg-background/70')}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-lg font-semibold tabular-nums tracking-normal">{value}</p>
    </div>
  )
}
